import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { DEFAULT_LIMIT_PAGE, DECIMAL_PLACES_DEFAULT } from '@/constants'
import {
  formatCurrency,
  formatDate,
  formatNumber,
  getTodayDateISO,
  onConfirmDangerMessage,
  trimString,
  useParamsState,
} from '@/helpers'
import {
  QUERIES_ADMIN,
  useDeleteForecast,
  useForecastList,
  useUpdateForecast,
} from '@/services'

import { SupplierForecastAgreementEnum, SupplierForecastProps } from '@/types'
import {
  Button,
  Card,
  CheckCircleOutilineIcon,
  EditableColumn,
  EditIcon,
  EyeIcon,
  FilterIcon,
  InputSearch,
  Pagination,
  Select,
  showToast,
  TableColumns,
  TableColumnsColumnProps,
  Tag,
  Text,
  Tooltip,
  TrashIcon,
  WarningOutlineIcon,
} from '@mercai/clever'

import { SupplierForecastsTableProps } from './supplier-forecasts-table.types'

import {
  AppliedFilters,
  EmptyState,
  SupplierForecastDrawerForm,
  SupplierProductCostExpirationTag,
} from '@/components'
import { ROUTES_ADMIN } from '@/router'
import { useQueryClient } from '@tanstack/react-query'
import { DrawerSupplierForecastFilter } from '..'
import { getUsedPercentage } from './supplier-forecasts-table.constants'
import styles from './supplier-forecasts-table.module.css'

export const SupplierForecastsTable = ({
  supplierUuid,
  withSupplierColumn = false,
  withTitle = true,
  withAddSupplierProduct = true,
  isEditable = false,
  params = {},
  onShow,
}: SupplierForecastsTableProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSuppliers')

  const todayDate = getTodayDateISO()

  const [filter, setFilter] = useParamsState({
    ...params,
    page: '1',
    limit: `${DEFAULT_LIMIT_PAGE}`,
    ...(supplierUuid ? { supplierProduct_Supplier_Uuid: supplierUuid } : {}),
    productTitle: '',
    catalogProduct_Uuid: '',
    search: '',
    expiresAtAfter: todayDate,
    costMin: '',
    costMax: '',
  })

  const [queryParams, setQueryParams] = useParamsState({
    ...params,
    supplierForecastform: '',
    updateSupplierForecastForm: '',
    updateSupplierForecastCatalogProductForm: '',
    supplierForecastFilterForm: '',
  })

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) =>
        !!filter[key] &&
        key !== 'page' &&
        key !== 'limit' &&
        key !== 'table' &&
        key !== 'offset' &&
        key !== 'supplierProduct_Supplier_Uuid' &&
        key !== 'supplierForecastFilterForm' &&
        key !== 'showDrawerShow' &&
        key !== 'tab' &&
        key !== 'ordering',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  const onHandleNewRegister = () => {
    setQueryParams({
      ...queryParams,
      supplierForecastform: 'true',
    })
  }

  const [totalPages, setTotalPages] = useState(1)

  const handleFilter = (changedKey: string, newValue: string) => {
    setFilter({ ...filter, [changedKey]: newValue })
  }

  const onHandleSortBy = (ordering: string) => {
    if (ordering === filter.ordering) {
      return handleFilter('ordering', '')
    }

    handleFilter('ordering', ordering)
  }

  const { data, isLoading, refetch } = useForecastList({
    params: {
      ...filter,
      limit: filter?.limit || DEFAULT_LIMIT_PAGE,
      ...(supplierUuid ? { supplierProduct_Supplier_Uuid: supplierUuid } : {}),
    },
  })

  const onCompleteCreate = async () => {
    await refetch()
  }

  const queryClient = useQueryClient()

  const { mutateAsync: mutateAsyncDeleteSupplierForecast } = useDeleteForecast()

  const { mutateAsync: mutateAsyncUpdateSupplierForecast } = useUpdateForecast()

  const onDeleteSupplierForecast = async (uuid: string) => {
    const confirm = await onConfirmDangerMessage(
      t('supplierForecastsDeleteConfirmTitle'),
      t('supplierForecastsDeleteConfirmDescription'),
      t('supplierForecastsDeleteConfirmConfirmButton'),
      t('supplierForecastsDeleteConfirmCancelButton'),
    )

    if (!confirm) {
      return
    }

    const response = await mutateAsyncDeleteSupplierForecast(uuid)

    if (response.success) {
      showToast({
        variant: 'warning',
        title: t('supplierForecastsTableDeleteSuccessTitle'),
        icon: <WarningOutlineIcon />,
      })

      queryClient.invalidateQueries({
        queryKey: [
          QUERIES_ADMIN.BFF_FORECAST.module,
          {
            ...filter,
            limit: filter?.limit || DEFAULT_LIMIT_PAGE,
          },
        ],
      })
    }
  }

  const onSupplierForecastSubmit = async (
    record: SupplierForecastProps,
    key: string,
    value: string | number | boolean | undefined,
  ) => {
    const response = await mutateAsyncUpdateSupplierForecast({
      uuid: record.uuid,
      [key]: value,
    })

    if (response.success) {
      showToast({
        variant: 'success',
        title: t('supplierForecastsTableUpdatedColumnSuccessTitle'),
        icon: <CheckCircleOutilineIcon />,
      })

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.BFF_FORECAST.module],
      })
    }
  }

  const supplierCategoriesObject = {
    metais: (
      <Tag variant="basic" model="outline" key="metais">
        {t('supplierForecastsTableCategoryMetais')}
      </Tag>
    ),
    resinas_plasticas: (
      <Tag variant="primary" model="outline" key="resinas_plasticas">
        {t('supplierForecastsTableCategoryResinasPlasticas')}
      </Tag>
    ),
  }

  const CategoryColumn = ({
    category,
  }: {
    category?: 'metais' | 'resinas_plasticas'
  }) => <div>{category ? supplierCategoriesObject?.[category] : '-'}</div>

  const columns: TableColumnsColumnProps<SupplierForecastProps>[] = [
    {
      title: t('supplierForecastsTableName'),
      key: 'name',
      render: ({ record }) => (
        <Tooltip
          text={
            record?.supplierProduct?.catalogProduct?.attributes?.title || ''
          }
          orientation="top"
          className={styles['product-column']}
        >
          <Link
            to={`/${ROUTES_ADMIN.CATALOG.PRODUCT.path}/${record?.supplierProduct?.catalogProduct?.uuid}`}
          >
            {trimString(
              record?.supplierProduct?.catalogProduct?.attributes?.title || '',
              16,
              '...',
            )}
          </Link>
        </Tooltip>
      ),
    },
    ...(withSupplierColumn
      ? [
        {
          title: t('supplierForecastsTableSupplierLabel'),
          key: 'supplierUuid',
          width: '9.6rem',
          render: ({ record }) => (
            <Tooltip
              text={record?.supplierProduct?.supplier?.name || ''}
              orientation="top"
              className={styles['supplier-column']}
            >
              <div>
                {trimString(
                  record?.supplierProduct?.supplier?.name || '',
                  14,
                  '...',
                )}
              </div>
            </Tooltip>
          ),
        } as TableColumnsColumnProps<SupplierForecastProps>,
      ]
      : ([] as TableColumnsColumnProps<SupplierForecastProps>[])),
    {
      title: t('supplierForecastsTableAvailable'),
      key: 'quantity',
      sortKey: 'quantity',
      width: '9rem',
      render: ({ record }) => {
        const renderContent = record?.quantity ? (
          <div className={styles.disponible}>
            <div>
              {record?.availableQuantity
                ? formatNumber(record?.availableQuantity, undefined, 0)
                : 0}{' '}
              Kg
            </div>
            <Text variant="text-small" className={styles['used-percentage']}>
              {getUsedPercentage(
                record?.quantity - record?.availableQuantity || 0,
                record?.quantity,
              )}
              % {t('supplierForecastsTableAvailableUsed')}
            </Text>
          </div>
        ) : (
          '-'
        )

        return isEditable ? (
          <EditableColumn
            type="number"
            inputProps={{
              decimalPrecision: 0,
            }}
            value={record?.quantity}
            onSubmit={async (value) =>
              onSupplierForecastSubmit(record, 'quantity', value)
            }
            render={renderContent}
          />
        ) : (
          renderContent
        )
      },
    },
    {
      title: t('supplierForecastsTableCost'),
      key: 'forecasts.cost',
      sortKey: 'cost',
      width: '13rem',
      render: ({ record }) => {
        const renderContent = record?.cost ? (
          <div className={styles['cost-column']}>
            {formatCurrency(
              record?.cost,
              'es-MX',
              record?.currency || 'USD',
              DECIMAL_PLACES_DEFAULT,
            )}
            <SupplierProductCostExpirationTag
              costExpirationDate={record?.expiresAt}
            />
          </div>
        ) : (
          '-'
        )

        return isEditable ? (
          <EditableColumn
            type="number"
            inputProps={{
              decimalPrecision: DECIMAL_PLACES_DEFAULT,
            }}
            value={record?.cost}
            onSubmit={async (value) =>
              onSupplierForecastSubmit(record, 'cost', value)
            }
            render={renderContent}
          />
        ) : (
          renderContent
        )
      },
    },
    {
      title: t('supplierForecastsTableAgreementLabel'),
      key: 'agreementType',
      sortKey: 'agreement_type',
      width: '8.5rem',
      render: ({ record }) => {
        const renderContent = (
          <div>
            {record?.agreementType
              ? tEnums(`forecast_agreement_${record?.agreementType}`)
              : '-'}
          </div>
        )

        return isEditable ? (
          <EditableColumn
            type="select"
            inputProps={{
              options: [
                {
                  label: t('selectEmpty'),
                  value: '',
                },
                ...Object.keys(SupplierForecastAgreementEnum).map((key) => ({
                  label: tEnums(`forecast_agreement_${key}`),
                  value: key,
                })),
              ],
            }}
            value={record?.agreementType}
            onSubmit={async (value) =>
              onSupplierForecastSubmit(record, 'agreementType', value)
            }
            render={renderContent}
          />
        ) : (
          renderContent
        )
      },
    },
    {
      title: t('supplierForecastsTableCostExpirationDateLabel'),
      key: 'expiresAt',
      sortKey: 'expires_at',
      width: '9rem',
      render: ({ record }) => {
        const renderContent = record?.expiresAt
          ? formatDate(record.expiresAt)
          : '-'
        return isEditable ? (
          <EditableColumn
            type="date-picker"
            value={record?.expiresAt}
            onSubmit={async (value) =>
              onSupplierForecastSubmit(record, 'expiresAt', value)
            }
            render={renderContent}
          />
        ) : (
          renderContent
        )
      },
    },
    {
      title: t('supplierForecastsTableLocation'),
      key: 'supplierWarehouseUuid',
      width: '12rem',
      render: ({ record }) => {
        const renderContent = (
          <div className={styles['warehouse-column']}>
            {record?.supplierProduct?.supplierWarehouse?.name ? (
              <>
                <img
                  src={
                    record?.supplierProduct?.supplierWarehouse?.country === 'US'
                      ? '/images/flag-us.svg'
                      : '/images/flag-mx.svg'
                  }
                  alt={'flag'}
                />
                {record?.supplierProduct?.supplierWarehouse?.name}
              </>
            ) : (
              '-'
            )}
          </div>
        )

        return renderContent
      },
    },
    {
      title: t('supplierForecastsTableCategory'),
      key: 'category',
      sortKey: 'category',
      width: '8rem',
      render: ({ record }) => (
        <CategoryColumn category={record?.supplierProduct?.category} />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: '10rem',
      render: ({ record }) => (
        <div className={styles['actions-column']}>
          <Button
            variant="secondary"
            model="round"
            onClick={() =>
              setQueryParams({
                ...queryParams,
                updateSupplierForecastForm: record?.uuid || '',
                updateSupplierForecastCatalogProductForm:
                  record?.supplierProduct?.catalogProduct?.uuid || '',
              })
            }
          >
            <EditIcon />
          </Button>
          <Button
            variant="danger"
            model="round"
            onClick={() =>
              record?.uuid ? onDeleteSupplierForecast(record.uuid) : null
            }
          >
            <TrashIcon />
          </Button>
          {onShow ? (
            <Button
              variant="primary"
              model="round"
              onClick={() => onShow(record?.uuid || '')}
            >
              <EyeIcon />
            </Button>
          ) : (
            <Link
              to={`/${ROUTES_ADMIN.CATALOG.PRODUCT.path}/${record?.supplierProduct?.catalogProduct?.uuid}`}
            >
              <Button variant="primary" model="round">
                <EyeIcon />
              </Button>
            </Link>
          )}
        </div>
      ),
    },
  ]

  const ExpandedComponent = ({ record }: { record: SupplierForecastProps }) => {
    return (
      <div className={styles['expanded-content']}>
        <div>
          <Text>
            <strong>{t('supplierForecastsTableMin')}</strong>
            {': '}
            {record?.supplierProduct?.minimumPurchaseQuantity
              ? `${record?.supplierProduct?.minimumPurchaseQuantity} Kg`
              : '-'}
          </Text>
          <Text>
            <strong>{t('supplierForecastsTablePacking')}</strong>
            {': '}
            {record?.supplierProduct?.package
              ? tEnums(
                `supplier_product_package_${record?.supplierProduct?.package}`,
              )
              : '-'}
          </Text>
          <Text>
            <strong>{t('supplierForecastsTableMinimumDeliveryTime')}</strong>
            {': '}
            {record?.supplierProduct?.minDeliveryDays
              ? `${record?.supplierProduct?.minDeliveryDays} ${t('supplierForecastsTableMinimumDeliveryTimeSuffix')}`
              : '-'}
          </Text>
          <Text>
            <strong>
              {t('supplierForecastsTableAgreementReferenceLabel')}
            </strong>
            {': '}
            {record?.agreementReference || '-'}
          </Text>
        </div>
        <Card className={styles.product}>
          <div>
            <Text variant="text-base">
              <strong>
                {record?.supplierProduct?.catalogProduct?.attributes?.title}
              </strong>
            </Text>
          </div>
          <div className={styles.row}>
            <Text>
              <strong>{t('supplierForecastsTableProductCategoryLabel')}</strong>{' '}
              {record?.supplierProduct?.catalogProduct?.category?.name}
            </Text>
          </div>

          {record?.supplierProduct?.catalogProduct?.reference && (
            <div className={styles.row}>
              <Text>
                <strong>
                  {t('supplierForecastsTableProductReferenceLabel')}
                </strong>{' '}
                {record?.supplierProduct?.catalogProduct.reference}
              </Text>
            </div>
          )}

          {record?.supplierProduct?.catalogProduct?.attributes?.fluidity && (
            <div className={styles.row}>
              <Text>
                <strong>
                  {t('supplierForecastsTableProductFluidityLabel')}
                </strong>{' '}
                {record?.supplierProduct?.catalogProduct.attributes.fluidity}{' '}
                g/10m
              </Text>
            </div>
          )}

          {record?.supplierProduct?.catalogProduct?.attributes?.density && (
            <div className={styles.row}>
              <Text>
                <strong>
                  {t('supplierForecastsTableProductDensityLabel')}
                </strong>{' '}
                {record?.supplierProduct?.catalogProduct.attributes.density}{' '}
                g/cm³
              </Text>
            </div>
          )}
        </Card>
      </div>
    )
  }

  useEffect(() => {
    if (data?.data?.count) {
      setTotalPages(Math.ceil(data?.data?.count / +filter.limit))
    }
  }, [data])

  return (
    <div>
      {withTitle && (
        <Text className={styles.title} variant="title-large">
          {t('supplierForecastsTitle')}
        </Text>
      )}
      <div className={styles['table-header']}>
        <div className={styles.filters}>
          <InputSearch
            value={filter.search}
            onChange={(value) => handleFilter('search', value)}
            translations={{
              inputPlaceholder: t('supplierForecastsTableSearchPlaceholder'),
            }}
          />

          <Button
            variant="secondary"
            onClick={() =>
              setQueryParams({
                ...queryParams,
                supplierForecastFilterForm: 'true',
              })
            }
          >
            <FilterIcon />
            {t('supplierForecastsTableFilters')}
            {getContentTotalFiltered()}
          </Button>
        </div>
        {withAddSupplierProduct && (
          <Button variant="light" onClick={onHandleNewRegister}>
            {t('supplierForecastsAddButton')}
          </Button>
        )}
      </div>

      <div className={styles['applied-filters']}>
        <AppliedFilters
          filtersApplied={filter}
          excludeKeys={[
            'page',
            'limit',
            'ordering',
            'supplierForecastFilterForm',
            'showDrawerShow',
            'supplierProduct_Supplier_Uuid',
            'table',
            'tab',
          ]}
          onRemoveFilter={(key) => handleFilter(key, '')}
          translations={{
            filterAppliedText: t('supplierForecastsTableFiltersAppliedFilters'),
          }}
          keyTranslations={{
            expiresAtBefore: t(
              'supplierForecastsTableFiltersCostExpirationDateBefore',
            ),
            expiresAtAfter: t(
              'supplierForecastsTableFiltersCostExpirationDateAfter',
            ),
            agreementType: t('supplierForecastsTableFiltersAgreementType'),
            supplierProduct_Supplier_Name: t(
              'supplierForecastsTableFiltersSupplierName',
            ),
            costMin: t('supplierProductsFilterCostMinLabel'),
            costMax: t('supplierProductsFilterCostMaxLabel'),
            quantityMin: t('supplierProductsFilterQuantityMinLabel'),
            quantityMax: t('supplierProductsFilterQuantityMaxLabel'),
          }}
          renderValue={(key, value) => {
            if (key === 'expiresAtBefore' || key === 'expiresAtAfter') {
              return formatDate(value, 'es-MX')
            }

            if (key === 'agreementType') {
              return tEnums(`forecast_agreement_${value}`)
            }

            return value
          }}
        />
      </div>

      <TableColumns<SupplierForecastProps>
        isLoading={isLoading}
        columns={columns}
        data={data?.data?.results || []}
        expandableComponent={ExpandedComponent}
        onSortBy={onHandleSortBy}
        sortBy={filter.ordering}
      />

      {!isLoading &&
        (!data?.data?.results || data?.data?.results?.length === 0) && (
          <EmptyState
            title={t('supplierForecastsTableEmptyTitle')}
            description=""
          />
        )}

      <div className={styles.pagination}>
        <div className={styles['pagination-show-registers']}>
          <Text>
            {t('supplierPaginationShowRegisters', {
              showNumber:
                (data?.data?.count || 0) < +filter.limit
                  ? data?.data?.count
                  : +filter.limit || 10,
              totalNumber: data?.data?.count || 0,
            })}
          </Text>

          <Select
            value={filter?.limit || `${DEFAULT_LIMIT_PAGE}`}
            onChange={(value) => handleFilter('limit', value)}
            options={[
              { label: '10', value: '10' },
              { label: '20', value: '20' },
              { label: '50', value: '50' },
              { label: '100', value: '100' },
            ]}
          />
        </div>

        <Pagination
          totalPages={totalPages}
          onChange={(page) => handleFilter('page', `${page}`)}
          currentPage={+filter.page as number}
        />
      </div>

      <DrawerSupplierForecastFilter
        filter={filter}
        onHandleFilter={setFilter}
        isOpen={!!queryParams.supplierForecastFilterForm}
        onHandleOpen={() => setQueryParams({ supplierForecastFilterForm: '' })}
      />

      <SupplierForecastDrawerForm
        isOpen={!!queryParams.updateSupplierForecastForm}
        onHandleOpen={() =>
          setQueryParams({
            ...queryParams,
            updateSupplierForecastForm: '',
            updateSupplierForecastCatalogProductForm: '',
          })
        }
        catalogProductUuid={
          queryParams.updateSupplierForecastCatalogProductForm
        }
        supplierForecastUuid={queryParams.updateSupplierForecastForm}
        onCompleteCreate={onCompleteCreate}
      />

      <SupplierForecastDrawerForm
        isOpen={!!queryParams.supplierForecastform}
        onHandleOpen={() =>
          setQueryParams({
            ...queryParams,
            supplierForecastform: '',
          })
        }
        onCompleteCreate={onCompleteCreate}
        supplierUuid={supplierUuid}
      />
    </div>
  )
}
